// Congés — « Vue d'ensemble ».
//
// L'onglet d'accueil de la page. Il répond aux trois questions qu'on se
// posait en ouvrant les autres onglets un par un:
//   · qui est en congé maintenant, et jusqu'à quand ?
//   · qu'est-ce qui attend une décision ?
//   · où le solde ne colle-t-il pas à la réalité ?
//
// Ce dernier point est le plus important: un congé posé depuis
// l'arbitrage de la Grille mensuelle ne débite RIEN. Le bloc « À
// régulariser » les expose et sait écrire le débit manquant.
import { useState } from 'react';
import {
  useRegularizeLeaves,
  type CongeDayDTO, type CongeOrigin, type LeavesOverviewDTO, type NegativeBalanceDTO,
  type OnLeavePersonDTO,
} from '../../api/hooks.js';
import { toast } from '../../store/toast.js';
import { cgAvColor, cgFmtLong, cgInitials, cgNum, entityById } from './common.js';

const ORIGIN_TONE: Record<CongeOrigin, string> = {
  demande: 'ok', saisie: 'ok', quotidien: 'warn', arbitrage: 'bad',
};

export function OverviewView({ data, loading, onOpen, onGoTo, onNew }: {
  data: LeavesOverviewDTO | undefined;
  loading: boolean;
  onOpen: (empId: string) => void;
  onGoTo: (view: 'calendrier' | 'demandes' | 'soldes') => void;
  onNew: () => void;
}) {
  if (loading) return <div className="cg-placeholder view-enter">Chargement de la vue d'ensemble…</div>;
  if (!data) return <div className="cg-placeholder view-enter">Impossible de charger la vue d'ensemble — réessayez.</div>;

  const nPending = data.pending.conge + data.pending.recup;

  return (
    <div className="cg-ov view-enter">
      {/* ── à régulariser: le seul bloc qui signale une incohérence ── */}
      {data.toRegularize.length > 0 && <RegularizeCard rows={data.toRegularize} onOpen={onOpen} />}

      {/* ── soldes sous zéro: le débit est passé, le crédit manque ── */}
      {data.negative.length > 0 && <NegativeCard rows={data.negative} onOpen={onOpen} />}

      <div className="cg-ov-grid">
        {/* ── qui est en congé maintenant ── */}
        <section className="cg-ovcard" data-testid="cg-onleave">
          <header className="cg-ovcard-h">
            <h3>En congé aujourd'hui<span className="gm-th-count">{data.onLeave.length}</span></h3>
            <button className="cg-ovlink" onClick={() => onGoTo('calendrier')}>Calendrier →</button>
          </header>
          {data.onLeave.length === 0
            ? <p className="cg-ovempty">Personne n'est en congé aujourd'hui.</p>
            : (
              <ul className="cg-ovlist">
                {data.onLeave.map((p) => <PersonRow key={p.employeeId + p.from} p={p} onOpen={onOpen} today={data.today} />)}
              </ul>
            )}
        </section>

        {/* ── départs à venir ── */}
        <section className="cg-ovcard" data-testid="cg-upcoming">
          <header className="cg-ovcard-h">
            <h3>Départs à venir<span className="gm-th-count">{data.upcoming.length}</span></h3>
            <span className="cg-ovhint">3 prochaines semaines</span>
          </header>
          {data.upcoming.length === 0
            ? <p className="cg-ovempty">Aucun départ programmé d'ici 3 semaines.</p>
            : (
              <ul className="cg-ovlist">
                {data.upcoming.slice(0, 8).map((p) => <PersonRow key={p.employeeId + p.from} p={p} onOpen={onOpen} today={data.today} upcoming />)}
              </ul>
            )}
        </section>

        {/* ── ce qui attend une décision ── */}
        <section className="cg-ovcard" data-testid="cg-pending">
          <header className="cg-ovcard-h">
            <h3>En attente de décision<span className="gm-th-count">{nPending + data.pending.claims}</span></h3>
            {nPending > 0 && <button className="cg-ovlink" onClick={() => onGoTo('demandes')}>Traiter →</button>}
          </header>
          {nPending + data.pending.claims === 0
            ? <p className="cg-ovempty">🎉 Rien n'attend de décision.</p>
            : (
              <ul className="cg-ovstats">
                {data.pending.conge > 0 && (
                  <li><b>{data.pending.conge}</b> demande{data.pending.conge > 1 ? 's' : ''} de congé <i>{cgNum(data.pending.days)} j au total</i></li>
                )}
                {data.pending.recup > 0 && (
                  <li><b>{data.pending.recup}</b> demande{data.pending.recup > 1 ? 's' : ''} de récup</li>
                )}
                {data.pending.claims > 0 && (
                  <li>
                    <b>{data.pending.claims}</b> journée{data.pending.claims > 1 ? 's' : ''} travaillée{data.pending.claims > 1 ? 's' : ''} à créditer
                    <i>à traiter dans Récupération</i>
                  </li>
                )}
              </ul>
            )}
        </section>

        {/* ── santé des soldes ── */}
        <section className="cg-ovcard" data-testid="cg-health">
          <header className="cg-ovcard-h">
            <h3>Soldes de l'équipe</h3>
            <button className="cg-ovlink" onClick={() => onGoTo('soldes')}>Détail →</button>
          </header>
          <ul className="cg-ovstats">
            <li><b>{cgNum(data.totals.solde)} j</b> de solde cumulé <i>{data.totals.employees} employés</i></li>
            <li><b>{cgNum(data.totals.recupSolde)} j</b> de stock récup</li>
            <li className={data.totals.low ? 'warn' : ''}>
              <b>{data.totals.low}</b> employé{data.totals.low > 1 ? 's' : ''} sous 5 jours de solde
            </li>
          </ul>
          <button className="cg-ghost full" onClick={onNew}>+ Saisir un congé</button>
        </section>
      </div>

      {/* ── derniers mouvements ── */}
      <section className="cg-ovcard wide" data-testid="cg-recent">
        <header className="cg-ovcard-h"><h3>Derniers mouvements</h3></header>
        {data.recent.length === 0
          ? <p className="cg-ovempty">Aucun mouvement enregistré.</p>
          : (
            <ul className="cg-ovlist">
              {data.recent.slice(0, 8).map((t) => (
                <li className="cg-ovrow" key={t.id} onClick={() => onOpen(t.employeeId)}>
                  <span className="cg-ovamount down gm-mono">{cgNum(t.amount)} j</span>
                  <span className="cg-ovrow-m">
                    <b>{cgFmtLong(t.date)}</b>
                    <i>{t.source}{t.reason ? ` — ${t.reason}` : ''}</i>
                  </span>
                  <span className="cg-ovtag">{t.type === 'RECUP' ? 'Récup' : 'Congé'}</span>
                </li>
              ))}
            </ul>
          )}
      </section>
    </div>
  );
}

function PersonRow({ p, onOpen, today, upcoming }: {
  p: OnLeavePersonDTO; onOpen: (id: string) => void; today: string; upcoming?: boolean;
}) {
  const ent = entityById(p.entityId);
  const when = upcoming
    ? `part le ${cgFmtLong(p.from)}${p.until && p.until !== p.from ? ` → ${cgFmtLong(p.until)}` : ''}`
    : p.until
      ? p.until === today
        ? 'dernier jour'
        : `jusqu'au ${cgFmtLong(p.until)}${p.daysLeft != null ? ` · ${p.daysLeft} j restant${p.daysLeft > 1 ? 's' : ''}` : ''}`
      : 'journée posée';
  return (
    <li className="cg-ovrow" onClick={() => onOpen(p.employeeId)}>
      <span className="gm-av sm" style={{ background: cgAvColor(p.employeeId) }}>{cgInitials(p.name)}</span>
      <span className="cg-ovrow-m">
        <b>{p.name}</b>
        <i>
          {ent && <span className="cg-entdot" style={{ background: ent.color }} />}
          {when}
        </i>
      </span>
      <span className={'cg-ovtag' + (p.type === 'RECUP' ? ' recup' : '')}>{p.type === 'RECUP' ? 'Récup' : 'Congé'}</span>
    </li>
  );
}

// ─── « Soldes négatifs » ─────────────────────────────────────
// La validation quotidienne débite même quand le compteur ne suit pas:
// une journée réellement posée ne doit pas être bloquée par un crédit
// pas encore importé. La contrepartie est ici — nommer qui est à
// découvert, pour créditer le stock plutôt que défaire la journée.
function NegativeCard({ rows, onOpen }: { rows: NegativeBalanceDTO[]; onOpen: (id: string) => void }) {
  const total = rows.reduce((a, r) => a + r.balance, 0);
  return (
    <div className="cg-regul neg" data-testid="cg-negative">
      <div className="cg-regul-h">
        <span className="cg-regul-ic">−</span>
        <div className="cg-regul-t">
          <b>{rows.length} solde{rows.length > 1 ? 's' : ''} sous zéro</b>
          <span>
            {cgNum(total)} j à découvert au total — des jours posés et débités que le compteur ne
            couvrait pas. À créditer (stock initial RH ou ajout manuel), pas à annuler.
          </span>
        </div>
      </div>
      <ul className="cg-regul-list">
        {rows.slice(0, 12).map((r) => {
          const ent = entityById(r.entityId);
          return (
            <li key={r.employeeId + r.type}>
              <span className="cg-ovrow-m" onClick={() => onOpen(r.employeeId)} style={{ cursor: 'pointer' }}>
                <b>{r.name}</b>
                <i>
                  {ent && <span className="cg-entdot" style={{ background: ent.color }} />}
                  {r.role ?? '—'}
                </i>
              </span>
              <span className="cg-origin bad gm-mono">{cgNum(r.balance)} j</span>
              <span className={'cg-ovtag' + (r.type === 'RECUP' ? ' recup' : '')}>
                {r.type === 'RECUP' ? 'Récup' : 'Congé'}
              </span>
            </li>
          );
        })}
        {rows.length > 12 && (
          <li className="cg-regul-more">+{rows.length - 12} autres soldes à découvert.</li>
        )}
      </ul>
    </div>
  );
}

// ─── « À régulariser » ───────────────────────────────────────
// Les jours posés qui n'ont jamais touché le compteur. Rien n'est écrit
// sans clic: le débit corrige un solde, il ne doit jamais surprendre.
function RegularizeCard({ rows, onOpen }: { rows: CongeDayDTO[]; onOpen: (id: string) => void }) {
  const regularize = useRegularizeLeaves();
  const [busy, setBusy] = useState<string | null>(null);
  const total = rows.reduce((a, r) => a + r.amount, 0);

  const run = async (items: CongeDayDTO[], key: string) => {
    setBusy(key);
    try {
      const res = await regularize.mutateAsync(items.map((i) => ({ employeeId: i.employeeId, date: i.date })));
      const negative = res.applied.filter((a) => a.balanceAfter < 0);
      toast.success(
        `${res.applied.length} jour${res.applied.length > 1 ? 's' : ''} débité${res.applied.length > 1 ? 's' : ''}`
        + (negative.length ? ` · ${negative.length} solde${negative.length > 1 ? 's' : ''} passe${negative.length > 1 ? 'nt' : ''} sous zéro` : ''),
      );
    } catch {
      toast.danger('Échec de la régularisation — réessayez.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="cg-regul" data-testid="cg-regularize">
      <div className="cg-regul-h">
        <span className="cg-regul-ic">!</span>
        <div className="cg-regul-t">
          <b>{rows.length} jour{rows.length > 1 ? 's' : ''} de congé sans débit du solde</b>
          <span>
            {cgNum(total)} j posés en paie qui n'ont jamais touché le compteur — surtout des arbitrages
            de la Grille mensuelle, qui classent la journée sans écrire au ledger.
          </span>
        </div>
        <button
          className="gm-attn-go"
          disabled={busy != null}
          onClick={() => run(rows, 'all')}
          data-testid="cg-regularize-all"
        >
          {busy === 'all' ? 'Régularisation…' : 'Tout régulariser'}
        </button>
      </div>
      <ul className="cg-regul-list">
        {rows.slice(0, 12).map((r) => (
          <li key={r.employeeId + r.date}>
            <span className="cg-ovrow-m" onClick={() => onOpen(r.employeeId)} style={{ cursor: 'pointer' }}>
              <b>{r.employeeName}</b>
              <i>{cgFmtLong(r.date)} · {cgNum(r.amount)} j {r.type === 'RECUP' ? 'récup' : 'congé'}</i>
            </span>
            <span className={'cg-origin ' + ORIGIN_TONE[r.origin]}>{r.label}</span>
            <button
              className="cg-iconbtn go"
              disabled={busy != null}
              title="Débiter le solde pour cette journée"
              onClick={() => run([r], r.employeeId + r.date)}
            >
              {busy === r.employeeId + r.date ? '…' : '↧'}
            </button>
          </li>
        ))}
        {rows.length > 12 && (
          <li className="cg-regul-more">+{rows.length - 12} autres jours — « Tout régulariser » les traite aussi.</li>
        )}
      </ul>
    </div>
  );
}

export { ORIGIN_TONE };
